import type { WebViewerInstance } from '@pdftron/webviewer';
import { useEffect } from 'react';

import { usePDFViewerRulesContext } from '../PDFViewerRulesContext';
import { createRuleButton } from '../consts';

type TextPopupButtonProps = {
  instance: WebViewerInstance | null;
  isPartLibraryViewer: boolean;
  isStandaloneViewer: boolean;
  isRequirementsReviewEnabled: boolean;
};

// Safe hook to use PDFViewerRulesContext that returns null if not available
const useSafePDFViewerRulesContext = () => {
  try {
    return usePDFViewerRulesContext();
  } catch {
    return null;
  }
};

// Hook for adding the Create Rule button to text popup
export const useTextPopupButton = ({
  instance,
  isPartLibraryViewer,
  isStandaloneViewer,
  isRequirementsReviewEnabled,
}: TextPopupButtonProps) => {
  const rulesContext = useSafePDFViewerRulesContext();

  useEffect(() => {
    // Early return for non-Quote Part Viewers or when context is not available
    if (isPartLibraryViewer || isStandaloneViewer || !rulesContext) {
      return;
    }

    if (!instance || !isRequirementsReviewEnabled) return;

    const { UI } = instance;

    // Only add the text popup button once
    const existingItems = UI.textPopup.getItems();
    const buttonExists = existingItems.some(
      (item: any) => item.dataElement === 'customTextAction',
    );

    if (!buttonExists) {
      UI.textPopup.add(
        [
          {
            type: 'actionButton',
            img: createRuleButton,
            title: 'Create Rule',
            onClick: () => {
              const selectedText =
                instance.Core.documentViewer.getSelectedText();
              if (selectedText && selectedText.trim()) {
                rulesContext.openModal(selectedText.trim());
              }
            },
            dataElement: 'customTextAction',
          },
        ],
        'linkButton',
      );
    }
  }, [
    instance,
    isPartLibraryViewer,
    isStandaloneViewer,
    isRequirementsReviewEnabled,
    rulesContext,
  ]);
};
